import { c as _c } from "react/compiler-runtime";
import Chip from '@precis-digital/kurama/Chip';
import { type ChipProps } from '@precis-digital/kurama/Chip';
import Tooltip, { TooltipTypography } from '@precis-digital/kurama/Tooltip';
import { styled } from '@precis-digital/kurama/utils';
import { type ReactElement, type ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
export const TOOL_CALL_DETAIL_CHIP_ROW_TEST_ID = 'tool-call-detail-chip-row';
const EllipsisLabel = styled('span')({
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});
const StyledChipRow = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '8px',
  // Share the action-card row with the label: take leftover width and allow
  // shrink so long chip labels can ellipsize instead of expanding the card.
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden'
});
const TooltipAnchor = styled('div')({
  display: 'inline-flex',
  maxWidth: '100%',
  minWidth: 0
});
const StyledDetailChip = styled(Chip)({
  maxWidth: '100%',
  minWidth: 0,
  overflow: 'hidden'
});
const isLabelClamped = (element: HTMLElement): boolean => element.scrollWidth - element.clientWidth > 1;
const getLabelPlainText = (label: ChipProps['label'], element: HTMLElement | null): string => {
  if (typeof label === 'string' || typeof label === 'number') {
    return String(label);
  }
  return element?.textContent?.trim() ?? '';
};

/**
 * Chip for Copilot tool-call detail rows. Long labels stay within the parent
 * (thinking-trace action cards) via ellipsis instead of overflowing. A tooltip
 * shows the full label only when it is actually clamped.
 */
export const ToolCallDetailChip = t0 => {
  const $ = _c(19);
  let label;
  let props;
  if ($[0] !== t0) {
    ({
      label,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = label;
    $[2] = props;
  } else {
    label = $[1];
    props = $[2];
  }
  const labelRef = useRef(null);
  const [isClamped, setIsClamped] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState("");
  let t1;
  if ($[3] !== label) {
    t1 = () => {
      const element = labelRef.current;
      if (element == null) {
        setIsClamped(false);
        setTooltipTitle("");
        return;
      }
      setIsClamped(isLabelClamped(element));
      setTooltipTitle(getLabelPlainText(label, element));
    };
    $[3] = label;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const measureClamp = t1;
  let t2;
  let t3;
  if ($[5] !== measureClamp) {
    t2 = () => {
      measureClamp();
      const element_0 = labelRef.current;
      if (element_0 == null || typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(() => {
        measureClamp();
      });
      observer.observe(element_0);
      return () => {
        observer.disconnect();
      };
    };
    t3 = [measureClamp];
    $[5] = measureClamp;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useLayoutEffect(t2, t3);
  const t4 = !isClamped || tooltipTitle === "";
  let t5;
  if ($[8] !== tooltipTitle) {
    t5 = <TooltipTypography variant="body3">{tooltipTitle}</TooltipTypography>;
    $[8] = tooltipTitle;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== label) {
    t6 = <EllipsisLabel ref={labelRef}>{label}</EllipsisLabel>;
    $[10] = label;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== props || $[13] !== t6) {
    t7 = <TooltipAnchor><StyledDetailChip {...props} label={t6} /></TooltipAnchor>;
    $[12] = props;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t7) {
    t8 = <Tooltip kind="multiline" disabled={t4} shouldDelay={true} width="fit-content" maxWidth="480px" allowOverflow={true} placement="top" content={t5}>{t7}</Tooltip>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};

/**
 * Flex row for one or more {@link ToolCallDetailChip}s — grows into leftover
 * action-card space and shrinks so chips can ellipsize.
 */
export const ToolCallDetailChipRow = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <StyledChipRow data-testid={TOOL_CALL_DETAIL_CHIP_ROW_TEST_ID}>{children}</StyledChipRow>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};